import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { alpha, Box, Chip, Divider, Link, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { RecallCheckMatchRecall } from '../../../services/goods-receiving/goods-receiving.types';
import { sourceHost } from '../../regulatory-news/utils/source';

interface ItemRecallWarningProps {
  recalls: RecallCheckMatchRecall[];
}

export function ItemRecallWarning({ recalls }: ItemRecallWarningProps) {
  const { t } = useTranslation('goodsReceiving');
  const theme = useTheme();

  if (recalls.length === 0) {
    return null;
  }

  const hasConfirmed = recalls.some((recall) => recall.matchLevel === 'CONFIRMED');
  const accent = hasConfirmed ? theme.palette.error.main : theme.palette.warning.main;
  const ink = hasConfirmed ? theme.palette.error.dark : theme.palette.warning.dark;

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        mt: 1,
        borderRadius: 2,
        border: '1px solid',
        borderColor: alpha(accent, 0.35),
        bgcolor: alpha(accent, 0.08),
        px: 1.5,
        py: 1.25,
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <ReportProblemOutlinedIcon aria-hidden sx={{ color: accent, fontSize: '1.15rem' }} />
        <Typography sx={{ fontWeight: 700, color: ink, fontSize: '0.85rem', lineHeight: 1.3 }}>
          {t('recall.title')}
        </Typography>
      </Stack>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: 'text.secondary',
          mt: 0.25,
          fontSize: '0.76rem',
          lineHeight: 1.4,
        }}
      >
        {t('recall.verifyHint')}
      </Typography>

      <Stack divider={<Divider sx={{ borderColor: alpha(accent, 0.2) }} />} sx={{ mt: 1 }}>
        {recalls.map((recall) => {
          const confirmed = recall.matchLevel === 'CONFIRMED';
          return (
            <Box key={recall.id} sx={{ py: 0.75 }}>
              <Chip
                size="small"
                color={confirmed ? 'error' : 'warning'}
                variant={confirmed ? 'filled' : 'outlined'}
                label={t(confirmed ? 'recall.confirmedMatch' : 'recall.possibleMatch')}
                sx={{ mb: 0.75, fontWeight: 700 }}
              />
              <Typography
                component="p"
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.35 }}
              >
                {recall.title}
              </Typography>
              {recall.brands.length > 0 && (
                <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('recall.brands', { value: recall.brands.join(', ') })}
                </Typography>
              )}
              {recall.lotNumbers.length > 0 && (
                <Typography
                  variant="caption"
                  component="p"
                  sx={{ color: confirmed ? 'error.main' : 'text.secondary', fontWeight: 600 }}
                >
                  {t('recall.recalledLots', { value: recall.lotNumbers.join(', ') })}
                </Typography>
              )}
              {recall.link ? (
                <Link
                  href={recall.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    minHeight: 44,
                    fontWeight: 600,
                    color: ink,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: { xs: '55vw', sm: 240 },
                    }}
                  >
                    {sourceHost(recall.link) ?? t('recall.sourceLabel')}
                  </Box>
                  <OpenInNewIcon aria-hidden sx={{ fontSize: 14, flexShrink: 0 }} />
                </Link>
              ) : null}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
